import { useRead, ReadState, isVideos, isStats } from "@/components/LoadingStates";
import { useAuth } from "@/context/AuthContext";
import { isLiveVideo } from "@/components/LiveDemoBadge";
import IncludeDemoToggle from "@/components/IncludeDemoToggle";
import useIncludeDemo from "@/hooks/useIncludeDemo";

import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";

import { Card } from "@/components/ui/card";
import { formatNumber } from "@/lib/format";
import { VIEWS_BY_PUBLICATION_TITLE, VIEWS_BY_PUBLICATION_NOTE, VIEWS_BY_PUBLICATION_SERIES, publicationDayLabel } from "@/lib/metricLabels";
import PlatformBadge from "@/components/PlatformBadge";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";

const PLATFORM_COLORS = {
  instagram: "hsl(var(--platform-instagram))",
  tiktok: "hsl(var(--platform-tiktok))",
  youtube: "hsl(var(--platform-youtube))",
};

export default function Analytics() {
  const { user } = useAuth();
  const [includeDemo, setIncludeDemo] = useIncludeDemo(user?.workspace_id);
  const statsRead = useRead("/dashboard/stats", isStats);
  const videosRead = useRead("/videos?limit=200&sort=recent", isVideos);
  const stats = statsRead.data;
  const rawVideos = videosRead.data || [];
  const videos = rawVideos.filter((v) => includeDemo || isLiveVideo(v));
  // Aggregate engagement rate by platform
  const byPlatform = {};
  for (const v of videos) {
    (byPlatform[v.platform] ||= []).push(v);
  }
  const engagementData = Object.entries(byPlatform).map(([p, arr]) => ({
    platform: p,
    engagement: +(arr.reduce((s, v) => s + v.engagement_rate, 0) / arr.length).toFixed(2),
    outlier_rate: +((arr.filter((v) => v.is_outlier).length / arr.length) * 100).toFixed(1),
  }));

  const pieData = stats ? stats.platforms.map((p) => ({ name: p.platform, value: p.outliers })) : [];

  // outlier multiplier distribution buckets
  const outlierVideos = videos.filter((v) => v.is_outlier);
  const buckets = [
    { range: "2.5-4x", min: 2.5, max: 4, count: 0 },
    { range: "4-6x", min: 4, max: 6, count: 0 },
    { range: "6-8x", min: 6, max: 8, count: 0 },
    { range: "8-10x", min: 8, max: 10, count: 0 },
    { range: "10x+", min: 10, max: Infinity, count: 0 },
  ];
  for (const v of outlierVideos) {
    const b = buckets.find((b) => v.outlier_multiplier >= b.min && v.outlier_multiplier < b.max);
    if (b) b.count += 1;
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <SectionTabs tabs={SECTION_TABS.home} activeKey="analytics" ariaLabel="Home sections" data-testid="section-tabs-home" />

      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Analytics & Trends</h1>
        <p className="text-muted-foreground text-sm mt-1">Cross-platform patterns and viral distribution.</p>
      </div>

      <IncludeDemoToggle checked={includeDemo} onChange={setIncludeDemo} />
      <p className="text-xs text-muted-foreground" data-testid="demo-filter-scope">Échantillon des vidéos récentes reçues : {videos.length} affichées sur {rawVideos.length}. Les graphiques locaux concernent cet échantillon de vidéos uniques. Multiplicateur : maximum des profils suivis, pas une mesure intrinsèque de l’auteur.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ReadState read={videosRead} scope="analytics-videos" empty={!videos.length} filtered={!!rawVideos.length && !includeDemo} title={rawVideos.length ? "Aucun contenu Live parmi les résultats reçus" : "Aucune vidéo dans l’échantillon reçu"} primaryCta={rawVideos.length ? { label: "Réinclure la démo", onClick: () => setIncludeDemo(true) } : { label: "Consulter les créateurs", to: "/creators" }}>
        <Card className="p-5">
          <h3 className="font-display font-bold text-lg mb-1">Avg Engagement Rate by Platform</h3>
          <p className="text-xs text-muted-foreground mb-4">Higher engagement often correlates with algorithmic lift.</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="platform" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} unit="%" />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="engagement" radius={[6, 6, 0, 0]} fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-bold text-lg mb-1">Outlier Multiplier Distribution</h3>
          <p className="text-xs text-muted-foreground mb-4">How rare are the very high performers?</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="range" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="hsl(var(--emerald))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        </ReadState>
        <ReadState read={statsRead} scope="analytics-stats" empty={stats?.total_videos === 0} title="Aucune vidéo disponible dans le catalogue" primaryCta={{ label: "Consulter les créateurs", to: "/creators" }}>
        <p className="text-xs text-muted-foreground lg:col-span-2">Agrégats serveur — tous les contenus. Le filtre démo/live ne s’applique pas à ces graphiques.</p>
        <Card className="p-5">
          <h3 className="font-display font-bold text-lg mb-1">Outlier Share by Platform</h3>
          <p className="text-xs text-muted-foreground mb-4">Where the biggest breakouts happen.</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={2}>
                  {pieData.map((d) => (
                    <Cell key={d.name} fill={PLATFORM_COLORS[d.name]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-bold text-lg mb-1">{VIEWS_BY_PUBLICATION_TITLE}</h3>
          <p className="text-xs text-muted-foreground mb-4" data-testid="analytics-views-explanation">{VIEWS_BY_PUBLICATION_NOTE}</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.trend || []} margin={{ left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => formatNumber(v)} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  labelFormatter={publicationDayLabel}
                  formatter={(v) => [formatNumber(v), VIEWS_BY_PUBLICATION_SERIES]}
                />
                <Line type="monotone" dataKey="views" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        </ReadState>
      </div>
    </div>
  );
}
